import { Locale, locales } from '@ardoq/locale';
import { clamp } from 'lodash';
import { type I18nLocale, t } from '@ardoq/i18n';
import { normalizeToLocalDate, parseDate } from './dateOperations';

const DEFAULT_TIME = '00:00:00.000';

const ISO8601StyleRegExp = /^\d{4}[/.-]\d{1,2}[/.-]\d{1,2}/i;
const localeAmbiguousDateRegExp =
  /^(\d{1,2})[/.,-]\s?(\d{1,2})[/.,-]\s?(\d{1,4})/i;
const timeRegExp = /\d{1,2}:\d{2}(:\d{2}(.\d{1,3})?)?(\s(AM|PM))?/i;

export const MAX_YEAR = 2999;
export const MIN_YEAR = 1946;
const MAX_MONTH = 12;
const MAX_DAY = 31;

export const getDateErrorMessage = (i18nLocale: I18nLocale) =>
  t(i18nLocale, 'dateTime:invalidDate', 'Invalid date');
export const getDateNotAllowedErrorMessage = (i18nLocale: I18nLocale) =>
  t(
    i18nLocale,
    'dateTime:dateNotAllowed',
    'This date is not within the valid limits'
  );

const isMDLocale = (locale: Locale) => locale.code === locales.enUS.code;
const getLocaleDayPosition = (locale: Locale) => (isMDLocale(locale) ? 2 : 1);
const getLocaleMonthPosition = (locale: Locale) => (isMDLocale(locale) ? 1 : 2);

const rangeCheckerMaker = (min: number, max: number) => (num: number) =>
  num > min && num <= max;
const isValidYearRange = rangeCheckerMaker(0, MAX_YEAR);
const isValidMonthRange = rangeCheckerMaker(0, MAX_MONTH);
const isValidDayRange = rangeCheckerMaker(0, MAX_DAY);

const getISO8601StringFromComponents = (
  year: string,
  month = '1',
  day = '1',
  time = DEFAULT_TIME
) => {
  return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(
    2,
    '0'
  )}T${time}`;
};

const isPMTime = (timeString: string) => /PM/i.test(timeString);
const isAMTime = (timeString: string) => /AM/i.test(timeString);

const convertPMTimeToAbsolute = (timeString: string) => {
  const [hours, minutes, rest] = timeString.split(':');
  if (hours === undefined || minutes === undefined) {
    return timeString;
  }
  const parsedHours = parseInt(hours, 10);
  // This conditional check is to cover the case if the time is "12" PM
  const hoursAbsolute = parsedHours + (parsedHours < 12 ? 12 : 0);
  const hoursAndMinutes = `${hoursAbsolute}:${minutes}`;
  if (rest) {
    return `${hoursAndMinutes}:${rest}`;
  }
  return hoursAndMinutes;
};

const convertAMTimeToAbsolute = (timeString: string) => {
  const [hours, minutes, rest] = timeString.split(':');
  if (hours === undefined || minutes === undefined) {
    return timeString;
  }
  const hoursWithMidnightFixed = hours === '12' ? `00` : hours;
  const hoursAndMinutes = `${hoursWithMidnightFixed}:${minutes}`;
  if (rest) {
    return `${hoursAndMinutes}:${rest}`;
  }
  return hoursAndMinutes;
};

const padHourIfNeeded = (timeString: string) => {
  if (timeString.split(':')[0].length === 1) {
    return `0${timeString}`;
  }
  return timeString;
};

const getCorrectedTime = (timeString: string) => {
  const time = timeString.match(/\d{1,2}:\d{2}(:\d{2}(.\d{1,3})?)?/)?.[0] ?? '';
  if (isPMTime(timeString)) {
    return convertPMTimeToAbsolute(time);
  } else if (isAMTime(timeString)) {
    return convertAMTimeToAbsolute(time);
  }
  return time;
};

const getTimeFromTimeString = (timeString = DEFAULT_TIME) => {
  const correctedTime = getCorrectedTime(timeString);
  return padHourIfNeeded(correctedTime);
};

const expandTwoDigitYear = (yearString: string): string => {
  if (yearString.length <= 2) {
    return `20${yearString.padStart(2, '0')}`;
  }
  return yearString;
};

const ardoqLocaleDateStringParser = (dateString: string, locale: Locale) => {
  const dateParts = dateString.match(localeAmbiguousDateRegExp);
  const time = getTimeFromTimeString(dateString.match(timeRegExp)?.[0]);
  if (!dateParts || dateParts.length < 4) {
    return null;
  }
  const year = expandTwoDigitYear(dateParts[3]);
  const month = dateParts[getLocaleMonthPosition(locale)];
  const day = dateParts[getLocaleDayPosition(locale)];

  if (
    !isValidYearRange(parseInt(year, 10)) ||
    !isValidMonthRange(parseInt(month, 10)) ||
    !isValidDayRange(parseInt(day, 10))
  ) {
    return null;
  }

  const dateISO = getISO8601StringFromComponents(year, month, day, time);
  const date = parseDate(dateISO);

  if (!isDateValid(date)) {
    return null;
  }

  return date;
};

export const parseLocaleDateString = (inputString: string, locale: Locale) => {
  const dateString = inputString?.trim();

  if (!dateString) return null;

  if (ISO8601StyleRegExp.test(dateString)) {
    // dates in this format are always year->month->day so no locale ambiguity can happen
    const date = parseDate(normalizeToLocalDate(dateString));

    return isDateValid(date) ? date : null;
  }

  if (localeAmbiguousDateRegExp.test(dateString)) {
    return ardoqLocaleDateStringParser(dateString, locale) ?? null;
  }

  const date = parseDate(normalizeToLocalDate(dateString));

  return isDateValid(date) ? date : null;
};

export const isDateValid = (date: Date) => {
  return !isNaN(date.getDate());
};

export const isDateStringValid = (dateString: string, locale: Locale) => {
  const date = parseLocaleDateString(dateString, locale);
  if (!date) {
    return false;
  }
  return isDateValid(date);
};

/**
 * @returns The year within a range
 * @params year - The input year value
 */
export const setYearWithinRange = (year: string | number) => {
  return clamp(Number(year), MIN_YEAR, MAX_YEAR);
};
